import React, { useMemo, useState, useEffect } from 'react';
import { moduleApi } from '../../lib/api';
import {
    X,
    Users,
    MapPin,
    Clock,
    CheckCircle2,
    AlertCircle,
    User,
    Building2,
    FileText,
    MessageSquare,
    Eye,
    Trash2,
    Paperclip,
    HardHat
} from 'lucide-react';
import { motion } from 'motion/react';
import { Button } from '../ui/Button';

interface Props {
    calendarId: string;
    month: number;
    year: number;
    onClose: () => void;
    onSuccess: () => void;
    initialEvent?: any | null;
    isBookerMode?: boolean;
}

interface StaffingRequirement {
    staffTypeId: string;
    staffTypeName: string;
    needed: number;
    assignedEmployeeIds: string[];
}

interface EventDocument {
    id?: string;
    file_name: string;
    mime_type?: string;
    file_size?: number;
    storage_key?: string;
}

interface AllowedTakerEmployee {
    id: string;
    first_name?: string;
    last_name?: string;
    email?: string;
}

interface CRMContactSuggestion {
    id: string;
    type?: 'PRIVÉ' | 'PROFESSIONNEL';
    first_name?: string;
    last_name?: string;
    company_name?: string;
    organizer_name?: string;
    phone?: string;
    email?: string;
}

export const EventForm = ({ calendarId, month, year, onClose, onSuccess, initialEvent = null, isBookerMode = false }: Props) => {
    const isEditMode = !!initialEvent;
    const [activeTab, setActiveTab] = useState<'infos' | 'documents' | 'notes'>('infos');
    const [type, setType] = useState<'PRIVÉ' | 'PROFESSIONNEL'>('PRIVÉ');
    
    // Calculate min and max dates for the month
    const minDate = `${year}-${String(month).padStart(2, '0')}-01`;
    const lastDay = new Date(year, month, 0).getDate();
    const maxDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

    // Default date: today if it's in the month, otherwise the first of the month
    const getDefaultDate = () => {
        const today = new Date();
        const yearNow = today.getFullYear();
        const monthNow = today.getMonth() + 1;
        const dayNow = today.getDate();
        
        if (yearNow === year && monthNow === month) {
            return `${yearNow}-${String(monthNow).padStart(2, '0')}-${String(dayNow).padStart(2, '0')}`;
        }
        return minDate;
    };

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        companyName: '',
        organizerName: '',
        phone: '',
        email: '',
        date: getDefaultDate(),
        startTime: '19:00',
        endTime: '02:00',
        numPeople: '',
        selectedSpaceId: '',
        crmContactId: ''
    });

    const [spaces, setSpaces] = useState<any[]>([]);
    const [staffTypes, setStaffTypes] = useState<any[]>([]);
    const [employees, setEmployees] = useState<any[]>([]);
    const [staffingRequirements, setStaffingRequirements] = useState<StaffingRequirement[]>([]);
    const [allowedEmployeesByCategory, setAllowedEmployeesByCategory] = useState<Record<string, string[]>>({});
    const [documents, setDocuments] = useState<EventDocument[]>([]);
    const [noteText, setNoteText] = useState('');
    // Envoi automatique de la demande aux prestataires dès qu'un extra externe est booké (au lieu de rester
    // "à demander" en attente d'un envoi manuel depuis le module Extras) — demande explicite d'Emeni (2026-08-19).
    const [autoSendExtras, setAutoSendExtras] = useState(false);
    const [trackTakenBy, setTrackTakenBy] = useState(false);
    const [allowedTakenByEmployees, setAllowedTakenByEmployees] = useState<AllowedTakerEmployee[]>([]);
    const [takenById, setTakenById] = useState('');
    const [lookupTerm, setLookupTerm] = useState('');
    const [lookupField, setLookupField] = useState<'firstName' | 'lastName' | 'companyName' | 'phone' | 'email' | null>(null);
    const [lookupLoading, setLookupLoading] = useState(false);
    const [crmSuggestions, setCrmSuggestions] = useState<CRMContactSuggestion[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Extras requis pour cette privatisation — indépendant du "Staffing (Gap Filling)" (retiré du
    // formulaire, remplacé par ce système, demande d'Emeni 2026-08-19). Un seul panneau, deux "genres" par
    // ligne (externe = partenaire, interne = salarié) — demande explicite d'Emeni de les garder au même
    // endroit plutôt qu'en deux blocs séparés. Rien n'est envoyé/confirmé/injecté au planning à la
    // sauvegarde : ça reste une simple liste de souhaits, à confirmer ensuite depuis le module Extras.
    const [extrasPartners, setExtrasPartners] = useState<{ id: string; name: string; types: { id: string; label: string; category_id?: string | null; scope_evenementiel?: boolean | number }[] }[]>([]);
    const [extrasInternalTypes, setExtrasInternalTypes] = useState<{ id: string; label: string; partner_name: string; employees: { employee_id: string; first_name: string; last_name: string }[] }[]>([]);
    const [extrasSelections, setExtrasSelections] = useState<{
        uid: string; kind: 'external' | 'internal';
        partnerId: string; extraTypeId: string; quantity: string; personIds: string[];
        internalTypeId: string; employeeIds: string[];
    }[]>([]);
    // "Besoin" par type (ex: 6 hôtesses nécessaires) — repère affiché dans la bulle du calendrier tant
    // qu'aucun extra n'est assigné pour ce type, jamais soustrait automatiquement. Demande d'Emeni, 2026-08-19.
    const [extrasNeeds, setExtrasNeeds] = useState<{ uid: string; extraTypeId: string; neededCount: string }[]>([]);
    // Horaire propre à ce type d'extra (ex: hôtesses arrivent 18h/partent 2h30 sur un événement 19h-2h) —
    // saisi ici par le demandeur en même temps que le besoin, jamais par celui qui booke ensuite (qui ne
    // fait que choisir qui). Vide = hérite des horaires de l'événement (comportement historique). Demande
    // d'Emeni.
    const [extrasTimeOverrides, setExtrasTimeOverrides] = useState<Record<string, { start: string; end: string }>>({});
    // Sous-groupes horaires additionnels par type (ex: 1 hôtesse arrive 18h/part 2h, 1 autre arrive
    // 19h/part 3h) — le "Besoin" ci-dessus + son horaire restent la ligne "par défaut" du type ; chaque
    // ligne additionnelle a sa propre quantité et son propre horaire, la somme des deux = besoin total du
    // type. Demande d'Emeni (2026-08-22) : "chaque type on peut écrire l'heure arriver heure départ".
    const [extraNeedSlots, setExtraNeedSlots] = useState<{ uid: string; extraTypeId: string; qty: number; start: string; end: string }[]>([]);
    // Valeur telle que préchargée à l'ouverture du formulaire — sert uniquement à afficher l'aperçu "3 → 4"
    // en direct à côté du compteur avant sauvegarde ; l'historique persistant complet (toutes les
    // modifications passées) est chargé à la demande via extrasNeedsHistory. Demande d'Emeni, 2026-08-20.
    const [initialNeededCounts, setInitialNeededCounts] = useState<Record<string, number>>({});
    const [showNeedsHistory, setShowNeedsHistory] = useState(false);
    const [extrasNeedsHistory, setExtrasNeedsHistory] = useState<{ type_label: string; old_count: number; new_count: number; changed_at: string }[]>([]);
    const [loadingNeedsHistory, setLoadingNeedsHistory] = useState(false);
    // Quantité déjà envoyée/confirmée chez un partenaire (status='demandee'), par type — même calcul que le
    // delta appliqué par le backend au moment du Booker, affiché ici en amont pour qu'on sache, avant même
    // de sauvegarder, exactement ce qui va réellement partir. Demande d'Emeni (2026-08-20), maquette validée.
    const [committedExternalByType, setCommittedExternalByType] = useState<Record<string, number>>({});
    // "Booker" est seul à donner accès à la sélection de personnes précises (externe/interne) — "Modifier"
    // ne permet d'ajuster que le chiffre du besoin, jamais qui est réellement booké. À la création, les deux
    // sont ouverts (comportement historique inchangé). Rappel répété d'Emeni (2026-08-20) : Modifier et
    // Booker ne font pas la même chose.
    const canSelectExtras = isBookerMode || !isEditMode;
    // Ne propose que les types marqués "Événementiel" dans le catalogue (Paramètres > Extras Partenaires) —
    // portée introduite le 2026-08-20, avant cette date tous les types de tous les partenaires étaient
    // montrés sans condition.
    const allExtraTypesFlat = extrasPartners.flatMap(p => p.types.filter(t => t.scope_evenementiel !== false && t.scope_evenementiel !== 0).map(t => ({ id: t.id, label: `${t.label} (${p.name})`, partnerId: p.id })));
    // extrasNeeds ne porte plus que la ligne "par défaut" de chaque type — le besoin TOTAL affiché/utilisé
    // partout ailleurs additionne les sous-groupes horaires (extraNeedSlots) par-dessus.
    const getDefaultNeededCount = (typeId: string) => parseInt(extrasNeeds.find(n => n.extraTypeId === typeId)?.neededCount || '0') || 0;
    const slotsForType = (typeId: string) => extraNeedSlots.filter(s => s.extraTypeId === typeId);
    const slotsQtySum = (typeId: string) => slotsForType(typeId).reduce((sum, s) => sum + (s.qty || 0), 0);
    const getNeededCount = (typeId: string) => getDefaultNeededCount(typeId) + slotsQtySum(typeId);
    const setNeededCount = (typeId: string, value: number) => {
        const clampedTotal = Math.max(0, value);
        const clamped = Math.max(0, clampedTotal - slotsQtySum(typeId));
        setExtrasNeeds(prev => {
            const existing = prev.find(n => n.extraTypeId === typeId);
            if (existing) return prev.map(n => n.extraTypeId === typeId ? { ...n, neededCount: String(clamped) } : n);
            return [...prev, { uid: `n${Date.now()}${Math.random().toString(36).slice(2, 6)}`, extraTypeId: typeId, neededCount: String(clamped) }];
        });
    };
    const addNeedSlot = (typeId: string) => {
        setExtraNeedSlots(prev => [...prev, { uid: `slot${Date.now()}${Math.random().toString(36).slice(2, 6)}`, extraTypeId: typeId, qty: 0, start: '', end: '' }]);
    };
    // Un sous-groupe horaire est une PARTIE du besoin, jamais un supplement : sur un besoin de 4 hotesses,
    // en placer 2 a 20h doit laisser 4 au total (2 a l'horaire par defaut + 2 a 20h). Jusqu'ici la quantite
    // du sous-groupe s'ajoutait sans etre retranchee de la ligne par defaut, et le besoin affiche passait a
    // 6 - "il comprend + 2 horaires differents" (Emeni, 2026-08-28). Le libelle du bouton disait pourtant
    // deja "pour UNE PARTIE du groupe". Les deux fonctions ci-dessous maintiennent donc le total constant :
    // ce qu'un sous-groupe prend, la ligne par defaut le perd, et le lui rend quand on le supprime.
    const ajusterLigneParDefaut = (typeId: string, ecart: number) => {
        if (!ecart) return;
        setExtrasNeeds(prev => prev.map(n => n.extraTypeId === typeId
            ? { ...n, neededCount: String(Math.max(0, (parseInt(n.neededCount) || 0) - ecart)) }
            : n));
    };
    const removeNeedSlot = (uid: string) => {
        const slot = extraNeedSlots.find(s => s.uid === uid);
        if (slot) ajusterLigneParDefaut(slot.extraTypeId, -(slot.qty || 0));
        setExtraNeedSlots(prev => prev.filter(s => s.uid !== uid));
    };
    const updateNeedSlot = (uid: string, patch: Partial<{ qty: number; start: string; end: string }>) => {
        const slot = extraNeedSlots.find(s => s.uid === uid);
        let qteFinale: number | undefined;
        if (patch.qty !== undefined && slot) {
            // Une partie du groupe ne peut pas depasser le groupe : on plafonne au total actuel du type,
            // sinon le besoin repartirait a la hausse par un autre chemin.
            qteFinale = Math.min(Math.max(0, patch.qty), getNeededCount(slot.extraTypeId));
            ajusterLigneParDefaut(slot.extraTypeId, qteFinale - (slot.qty || 0));
        }
        setExtraNeedSlots(prev => prev.map(s => {
            if (s.uid !== uid) return s;
            if (qteFinale !== undefined) return { ...s, ...patch, qty: qteFinale };
            return { ...s, ...patch };
        }));
    };
    // Assigné actuellement par type — UNIQUEMENT les salariés internes décomptent du "Besoin" restant : un
    // extra externe reste une simple demande envoyée au partenaire (pas encore confirmée), donc le besoin
    // doit rester visible/entier jusqu'à confirmation réelle — contrairement à l'interne, où sélectionner un
    // salarié EST la confirmation (c'est déjà ton propre personnel). Précision explicite d'Emeni (2026-08-19,
    // après la 3e refonte) : "un extra externe reste un extra externe", il ne doit jamais se retirer du
    // compteur "Besoin" ici. Recalculé à chaque rendu pour que le compteur descende en direct côté interne.
    const assignedCountByType = new Map<string, number>();
    extrasSelections.forEach(s => {
        if (s.kind === 'internal' && s.internalTypeId) assignedCountByType.set(s.internalTypeId, (assignedCountByType.get(s.internalTypeId) || 0) + s.employeeIds.length);
    });
    const remainingForType = (typeId: string) => Math.max(0, getNeededCount(typeId) - (assignedCountByType.get(typeId) || 0));
    const toggleNeedsHistory = () => {
        const next = !showNeedsHistory;
        setShowNeedsHistory(next);
        if (next && isEditMode && initialEvent?.id) {
            setLoadingNeedsHistory(true);
            moduleApi.getEvenementielExtrasNeedsHistory(initialEvent.id)
                .then((d: any) => setExtrasNeedsHistory(Array.isArray(d) ? d : []))
                .catch(() => setExtrasNeedsHistory([]))
                .finally(() => setLoadingNeedsHistory(false));
        }
    };
    // Personnes enregistrées par type d'extra (clé = extra_type_id), chargées à la demande quand un type
    // est choisi sur une ligne — sélection optionnelle, demande d'Emeni (2026-08-19).
    const [extrasPeopleCache, setExtrasPeopleCache] = useState<Record<string, { id: string; first_name: string; last_name: string; already_used: boolean }[]>>({});
    const loadExtrasPeople = (partnerId: string, extraTypeId: string) => {
        if (extrasPeopleCache[extraTypeId]) return;
        moduleApi.getExtrasPartnerPeople(partnerId, extraTypeId).then((d: any) => {
            setExtrasPeopleCache(prev => ({ ...prev, [extraTypeId]: Array.isArray(d) ? d : [] }));
        }).catch(() => {});
    };

    // Sélection en pastilles directes (personnes enregistrées côté externe, salariés côté interne) — plus
    // de ligne dropdown+quantité+bouton "Ajouter", jugé trop répétitif par Emeni (2026-08-19, 2e refonte du
    // panneau). Une pastille cliquée crée/complète la sélection du type ; la retirer la réduit, et supprime
    // la ligne entièrement si elle redevient vide — le compteur "Besoin" (dérivé de extrasSelections à
    // chaque rendu, jamais une valeur à part) remonte donc automatiquement tout seul en la désélectionnant.
    const toggleExternalPerson = (typeId: string, partnerId: string, personId: string) => {
        setExtrasSelections(prev => {
            const existing = prev.find(s => s.kind === 'external' && s.extraTypeId === typeId);
            if (!existing) {
                return [...prev, {
                    uid: `x${Date.now()}${Math.random().toString(36).slice(2, 6)}`, kind: 'external' as const,
                    partnerId, extraTypeId: typeId, quantity: '1', personIds: [personId],
                    internalTypeId: '', employeeIds: [] as string[],
                }];
            }
            const nextPersonIds = existing.personIds.includes(personId)
                ? existing.personIds.filter(id => id !== personId)
                : [...existing.personIds, personId];
            if (nextPersonIds.length === 0) return prev.filter(s => s.uid !== existing.uid);
            return prev.map(s => s.uid === existing.uid ? { ...s, personIds: nextPersonIds, quantity: String(nextPersonIds.length) } : s);
        });
    };
    const toggleInternalEmployee = (typeId: string, employeeId: string) => {
        setExtrasSelections(prev => {
            const existing = prev.find(s => s.kind === 'internal' && s.internalTypeId === typeId);
            if (!existing) {
                return [...prev, {
                    uid: `x${Date.now()}${Math.random().toString(36).slice(2, 6)}`, kind: 'internal' as const,
                    partnerId: '', extraTypeId: '', quantity: '1', personIds: [] as string[],
                    internalTypeId: typeId, employeeIds: [employeeId],
                }];
            }
            const nextEmployeeIds = existing.employeeIds.includes(employeeId)
                ? existing.employeeIds.filter(id => id !== employeeId)
                : [...existing.employeeIds, employeeId];
            if (nextEmployeeIds.length === 0) return prev.filter(s => s.uid !== existing.uid);
            return prev.map(s => s.uid === existing.uid ? { ...s, employeeIds: nextEmployeeIds } : s);
        });
    };

    useEffect(() => {
        loadData();
    }, []);

    // Précharge les personnes enregistrées de TOUS les types externes dès que le catalogue est connu — les
    // pastilles s'affichent directement, sans plus dépendre d'une sélection de ligne pour déclencher le
    // chargement (l'ancien mécanisme dropdown+"Ajouter" a disparu).
    useEffect(() => {
        extrasPartners.forEach(p => p.types.forEach(t => loadExtrasPeople(p.id, t.id)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [extrasPartners]);

    useEffect(() => {
        const q = lookupTerm.trim();
        if (q.length < 2) {
            setCrmSuggestions([]);
            setLookupLoading(false);
            return;
        }

        let cancelled = false;
        setLookupLoading(true);

        const timer = setTimeout(async () => {
            try {
                const items = await moduleApi.searchCRMContacts(q);
                if (!cancelled) {
                    setCrmSuggestions(Array.isArray(items) ? items : []);
                }
            } catch {
                if (!cancelled) {
                    setCrmSuggestions([]);
                }
            } finally {
                if (!cancelled) {
                    setLookupLoading(false);
                }
            }
        }, 250);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [lookupTerm]);

    const loadData = async () => {
        try {
            const [config, e, mappings, partners, internalTypes] = await Promise.all([
                moduleApi.getEvenementielConfig(),
                moduleApi.getEmployes(),
                moduleApi.getStaffCategoryMappings(),
                moduleApi.getExtrasPartners().catch(() => []),
                moduleApi.getExtrasInternalTypes().catch(() => [])
            ]);
            // scope_evenementiel conservé : sans lui, le filtre de allExtraTypesFlat (portée du catalogue,
            // 2026-08-20) ne voyait jamais le champ et laissait passer tous les types sans distinction — bug
            // introduit par cette même fonctionnalité, corrigé dans la foulée.
            setExtrasPartners(Array.isArray(partners) ? partners.map((p: any) => ({ id: p.id, name: p.name, types: (Array.isArray(p.types) ? p.types : []).map((t: any) => ({ id: t.id, label: t.label, category_id: t.category_id, scope_evenementiel: t.scope_evenementiel })) })) : []);
            setExtrasInternalTypes(Array.isArray(internalTypes) ? internalTypes.filter((t: any) => Array.isArray(t.employees) && t.employees.length > 0).map((t: any) => ({ id: t.id, label: t.label, partner_name: t.partner_name, employees: t.employees })) : []);

            // Précharge les extras encore modifiables (jamais envoyés/confirmés) en mode édition — sans ça,
            // chaque sauvegarde recréait les mêmes extras en double (bug signalé par Emeni, 2026-08-19).
            if (isEditMode && initialEvent?.id) {
                try {
                    const existing: any = await moduleApi.getEvenementielExtras(initialEvent.id);
                    const committedMap: Record<string, number> = {};
                    (existing?.committed_external || []).forEach((r: any) => { committedMap[r.extra_type_id] = Number(r.quantity) || 0; });
                    setCommittedExternalByType(committedMap);
                    const externalSelections = (existing?.external || []).map((r: any) => ({
                        uid: `x${Date.now()}${Math.random().toString(36).slice(2, 6)}`, kind: 'external' as const,
                        partnerId: r.partner_id, extraTypeId: r.extra_type_id, quantity: String(r.quantity || 1), personIds: r.person_ids || [],
                        internalTypeId: '', employeeIds: [] as string[],
                    }));
                    // Regroupe les bookings internes par type (une ligne = un type + ses salariés sélectionnés
                    // en pastilles, plutôt qu'une ligne par salarié).
                    const internalByType = new Map<string, string[]>();
                    (existing?.internal || []).forEach((r: any) => {
                        if (!internalByType.has(r.extra_type_id)) internalByType.set(r.extra_type_id, []);
                        internalByType.get(r.extra_type_id)!.push(r.employee_id);
                    });
                    const internalSelections = Array.from(internalByType.entries()).map(([extraTypeId, employeeIds]) => ({
                        uid: `x${Date.now()}${Math.random().toString(36).slice(2, 6)}`, kind: 'internal' as const,
                        partnerId: '', extraTypeId: '', quantity: '1', personIds: [] as string[],
                        internalTypeId: extraTypeId, employeeIds,
                    }));
                    setExtrasSelections([...externalSelections, ...internalSelections]);
                    externalSelections.forEach((s: any) => { if (s.partnerId && s.extraTypeId) loadExtrasPeople(s.partnerId, s.extraTypeId); });
                    // Un type peut désormais être réparti sur plusieurs lignes (extras_event_needs n'est plus
                    // fusionné côté backend) — la 1ère ligne de chaque type redevient la ligne "par défaut"
                    // (compteur + horaire déjà affichés ici), les suivantes deviennent des sous-groupes
                    // horaires additionnels (extraNeedSlots). Demande d'Emeni (2026-08-22).
                    const needsData = existing?.needs || initialEvent?.extras_needs || [];
                    const needsByType = new Map<string, any[]>();
                    needsData.forEach((n: any) => {
                        if (!needsByType.has(n.extra_type_id)) needsByType.set(n.extra_type_id, []);
                        needsByType.get(n.extra_type_id)!.push(n);
                    });
                    const defaultNeeds: { uid: string; extraTypeId: string; neededCount: string }[] = [];
                    const timeOverrides: Record<string, { start: string; end: string }> = {};
                    const initialCounts: Record<string, number> = {};
                    const slots: { uid: string; extraTypeId: string; qty: number; start: string; end: string }[] = [];
                    needsByType.forEach((rows, typeId) => {
                        const [first, ...rest] = rows;
                        defaultNeeds.push({ uid: `n${Date.now()}${Math.random().toString(36).slice(2, 6)}`, extraTypeId: typeId, neededCount: String(first.needed_count || 0) });
                        timeOverrides[typeId] = { start: first.start_time_override || '', end: first.end_time_override || '' };
                        initialCounts[typeId] = rows.reduce((sum: number, r: any) => sum + (Number(r.needed_count) || 0), 0);
                        rest.forEach((r: any) => {
                            slots.push({ uid: `slot${Date.now()}${Math.random().toString(36).slice(2, 6)}`, extraTypeId: typeId, qty: Number(r.needed_count) || 0, start: r.start_time_override || '', end: r.end_time_override || '' });
                        });
                    });
                    setExtrasNeeds(defaultNeeds);
                    setExtrasTimeOverrides(timeOverrides);
                    setInitialNeededCounts(initialCounts);
                    setExtraNeedSlots(slots);
                } catch (e) { console.error('Failed to preload extras', e); }
            }
            const configuredSpaces = config?.spaces || [];
            const authorizedCategories = config?.authorized_staff_categories || [];
            const employeeList = Array.isArray(e) ? e : [];
            const allowedTakerIds = Array.isArray(config?.allowed_taker_employee_ids)
                ? config.allowed_taker_employee_ids.map((id: any) => String(id))
                : [];
            const allowedTakersFromApi = Array.isArray(config?.allowed_taken_by_employees)
                ? config.allowed_taken_by_employees
                : [];
            const allowedTakers = allowedTakersFromApi.length > 0
                ? allowedTakersFromApi
                : employeeList
                    .filter((emp: any) => allowedTakerIds.includes(String(emp.id)))
                    .map((emp: any) => ({
                        id: String(emp.id),
                        first_name: emp.first_name || '',
                        last_name: emp.last_name || '',
                        email: emp.email || ''
                    }));

            setSpaces(configuredSpaces);
            setStaffTypes(authorizedCategories);
            setEmployees(employeeList);
            setTrackTakenBy(!!config?.track_taken_by);
            setAllowedTakenByEmployees(allowedTakers);

            const groupedMappings: Record<string, string[]> = {};
            (mappings || []).forEach((row: any) => {
                if (!groupedMappings[row.staff_category_id]) {
                    groupedMappings[row.staff_category_id] = [];
                }
                groupedMappings[row.staff_category_id].push(row.employee_id);
            });
            setAllowedEmployeesByCategory(groupedMappings);
            
            setStaffingRequirements(authorizedCategories.map((type: any) => ({
                staffTypeId: type.id,
                staffTypeName: type.name,
                needed: 0,
                assignedEmployeeIds: []
            })));

            if (initialEvent) {
                const start = new Date(initialEvent.start_time);
                const end = new Date(initialEvent.end_time);

                // Helper to get local YYYY-MM-DD
                const getLocalDateStr = (d: Date) => {
                    if (isNaN(d.getTime())) return getDefaultDate();
                    const year = d.getFullYear();
                    const month = String(d.getMonth() + 1).padStart(2, '0');
                    const day = String(d.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                };

                // Helper to get local HH:mm
                const getLocalTimeStr = (d: Date) => {
                    if (isNaN(d.getTime())) return '19:00';
                    const hours = String(d.getHours()).padStart(2, '0');
                    const minutes = String(d.getMinutes()).padStart(2, '0');
                    return `${hours}:${minutes}`;
                };

                setType(initialEvent.type || 'PRIVÉ');
                setFormData({
                    firstName: initialEvent.first_name || '',
                    lastName: initialEvent.last_name || '',
                    companyName: initialEvent.company_name || '',
                    organizerName: initialEvent.organizer_name || '',
                    phone: initialEvent.phone || '',
                    email: initialEvent.email || '',
                    date: getLocalDateStr(start),
                    startTime: getLocalTimeStr(start),
                    endTime: getLocalTimeStr(end),
                    numPeople: String(initialEvent.num_people || ''),
                    selectedSpaceId: initialEvent.spaces?.[0]?.id || '',
                    crmContactId: initialEvent.crm_contact_id || ''
                });

                setDocuments(Array.isArray(initialEvent.documents) ? initialEvent.documents : []);
                setNoteText(initialEvent.note_text || '');
                setTakenById(initialEvent.taken_by_id || '');
                setAutoSendExtras(!!initialEvent.auto_send_extras);

                const requirementsByType: Record<string, StaffingRequirement> = {};
                authorizedCategories.forEach((staffType: any) => {
                    requirementsByType[staffType.id] = {
                        staffTypeId: staffType.id,
                        staffTypeName: staffType.name,
                        needed: 0,
                        assignedEmployeeIds: []
                    };
                });

                (initialEvent.staff || []).forEach((item: any) => {
                    if (!requirementsByType[item.staff_type_id]) return;
                    requirementsByType[item.staff_type_id].needed = Number(item.count || 0);
                });

                (initialEvent.assignments || []).forEach((assignment: any) => {
                    const req = requirementsByType[assignment.staff_type_id];
                    if (req && assignment.employee_id) {
                        req.assignedEmployeeIds.push(assignment.employee_id);
                    }
                });

                setStaffingRequirements(Object.values(requirementsByType));
            }
        } catch (err) {
            console.error(err);
        }
    };

    const updateStaffingNeed = (staffTypeId: string, needed: number) => {
        setStaffingRequirements(prev => prev.map(req => 
            req.staffTypeId === staffTypeId ? { ...req, needed } : req
        ));
    };

    const toggleAssignment = (employeeId: string, staffTypeId: string) => {
        setStaffingRequirements(prev => prev.map(req => {
            if (req.staffTypeId === staffTypeId) {
                const isAssigned = req.assignedEmployeeIds.includes(employeeId);
                return {
                    ...req,
                    assignedEmployeeIds: isAssigned
                        ? req.assignedEmployeeIds.filter(id => id !== employeeId)
                        : [...req.assignedEmployeeIds, employeeId]
                };
            }
            return req;
        }));
    };

    const addFiles = (files: FileList | null) => {
        if (!files) return;
        const nextDocs: EventDocument[] = [];
        for (const file of Array.from(files)) {
            nextDocs.push({
                file_name: file.name,
                mime_type: file.type,
                file_size: file.size,
                storage_key: URL.createObjectURL(file)
            });
        }
        setDocuments(prev => [...nextDocs, ...prev]);
    };

    const removeDocument = (index: number) => {
        setDocuments(prev => prev.filter((_, i) => i !== index));
    };

    const openDocument = (doc: EventDocument) => {
        if (!doc.storage_key) {
            alert('Document sans URL de visualisation.');
            return;
        }
        window.open(doc.storage_key, '_blank');
    };

    const triggerLookup = (field: 'firstName' | 'lastName' | 'companyName' | 'phone' | 'email', value: string) => {
        setLookupField(field);
        setLookupTerm(value);
    };

    const applyCRMContact = (contact: CRMContactSuggestion) => {
        if (contact.type === 'PRIVÉ' || contact.type === 'PROFESSIONNEL') {
            setType(contact.type);
        }

        setFormData(prev => ({
            ...prev,
            firstName: contact.first_name || prev.firstName,
            lastName: contact.last_name || prev.lastName,
            companyName: contact.company_name || prev.companyName,
            organizerName: contact.organizer_name || prev.organizerName,
            phone: contact.phone || prev.phone,
            email: contact.email || prev.email,
            crmContactId: contact.id
        }));

        setCrmSuggestions([]);
        setLookupTerm('');
        setLookupField(null);
    };

    const renderCRMSearchSuggestions = (field: 'firstName' | 'lastName' | 'companyName' | 'phone' | 'email') => {
        if (lookupField !== field) return null;
        if (!lookupLoading && crmSuggestions.length === 0) return null;

        return (
            <div className="mt-1 rounded-lg border border-gray-300 dark:border-border bg-white dark:bg-surface shadow-lg max-h-48 overflow-y-auto">
                {lookupLoading && (
                    <div className="px-3 py-2 text-[10px] text-slate-500 dark:text-content-muted">Recherche CRM...</div>
                )}
                {!lookupLoading && crmSuggestions.map((contact) => {
                    const title = contact.type === 'PROFESSIONNEL'
                        ? (contact.company_name || 'Entreprise')
                        : [contact.first_name, contact.last_name].filter(Boolean).join(' ').trim() || 'Particulier';
                    const secondaryParts = [contact.phone, contact.email].filter(Boolean);

                    return (
                        <button
                            key={contact.id}
                            type="button"
                            onMouseDown={() => applyCRMContact(contact)}
                            className="w-full text-left px-3 py-2 border-b last:border-b-0 border-border hover:bg-slate-100 dark:hover:bg-surface-soft transition-colors"
                        >
                            <div className="text-[10px] font-bold text-slate-900 dark:text-content uppercase truncate">{title}</div>
                            {!!contact.organizer_name && (
                                <div className="text-[10px] text-slate-600 dark:text-content-muted truncate">Organisateur: {contact.organizer_name}</div>
                            )}
                            {secondaryParts.length > 0 && (
                                <div className="text-[10px] text-slate-500 dark:text-content-muted truncate">{secondaryParts.join(' • ')}</div>
                            )}
                        </button>
                    );
                })}
            </div>
        );
    };

    const normalizedEmployees = useMemo(() => {
        return employees.map((emp: any) => {
            let tags: string[] = [];
            if (Array.isArray(emp.tags)) {
                tags = emp.tags;
            } else if (typeof emp.tags === 'string' && emp.tags.trim()) {
                try {
                    const parsed = JSON.parse(emp.tags);
                    tags = Array.isArray(parsed) ? parsed : [];
                } catch {
                    tags = [emp.tags];
                }
            }
            const displayName = [emp.first_name, emp.last_name].filter(Boolean).join(' ').trim() || emp.name || emp.email || emp.id;
            return { ...emp, parsedTags: tags.map((t: string) => String(t).toLowerCase()), displayName };
        });
    }, [employees]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const staff_requests: Record<string, number> = {};
            const assignments: any[] = [];

            staffingRequirements.forEach(req => {
                if (req.needed > 0) {
                    staff_requests[req.staffTypeId] = req.needed;
                    req.assignedEmployeeIds.forEach(empId => {
                        assignments.push({ employee_id: empId, staff_type_id: req.staffTypeId });
                    });
                }
            });

            // Combine date and times
            const start_time = `${formData.date}T${formData.startTime}:00`;
            let end_time = `${formData.date}T${formData.endTime}:00`;

            // If end time is earlier than start time, assume it's the next day
            if (formData.endTime < formData.startTime) {
                const nextDay = new Date(formData.date);
                nextDay.setDate(nextDay.getDate() + 1);
                const y = nextDay.getFullYear();
                const m = String(nextDay.getMonth() + 1).padStart(2, '0');
                const d = String(nextDay.getDate()).padStart(2, '0');
                end_time = `${y}-${m}-${d}T${formData.endTime}:00`;
            }

            const requiresSpaceSelection = spaces.length > 0;
            const fallbackSpaceId = String(formData.selectedSpaceId || '').trim();
            if (requiresSpaceSelection && !fallbackSpaceId) {
                setError('Veuillez sélectionner un espace pour cette privatisation.');
                setActiveTab('infos');
                setLoading(false);
                return;
            }

            const internal_extras = extrasSelections
                .filter(s => s.kind === 'internal' && s.internalTypeId)
                .flatMap(s => s.employeeIds.map(employeeId => ({ extra_type_id: s.internalTypeId, employee_id: employeeId })));

            // UNE LIGNE DE DEMANDE PAR SOUS-GROUPE HORAIRE, et non plus une seule par poste.
            // Bug réel constaté le 2026-08-25 sur la privatisation du 8 septembre : le besoin était bien
            // "2 hôtesses 12h-18h + 2 hôtesses 18h-00h", mais seules 2 hôtesses partaient en demande — le
            // bloc "+ horaire différent" (extraNeedSlots) ne servait qu'à enregistrer le besoin, il n'était
            // jamais relu ici. La moitié du personnel n'était donc commandée à personne, sans aucun signal.
            // Le rang de chaque ligne (0 = l'horaire principal, 1..n = les horaires différents dans l'ordre
            // de saisie) accompagne la demande jusqu'en base (need_slot_index), pour que corriger l'horaire
            // d'un sous-groupe ne recale plus que ses propres demandes.
            const needTypeIds: string[] = [];
            [...extrasNeeds.map(n => n.extraTypeId), ...extraNeedSlots.map(s => s.extraTypeId)]
                .forEach(id => { if (id && !needTypeIds.includes(id)) needTypeIds.push(id); });

            // SOURCE UNIQUE des lignes de besoin d'un poste, dans l'ordre : l'horaire principal d'abord,
            // puis chaque "horaire différent". C'est ce tableau qui numérote les sous-groupes, et il sert
            // à la fois à enregistrer le besoin ET à créer les demandes — les deux ne peuvent donc plus se
            // désynchroniser. Les lignes à 0 sont écartées ici, une fois pour toutes, sinon un poste dont
            // l'horaire principal est vide aurait vu son besoin numéroté autrement que ses demandes.
            const needLinesByType = needTypeIds.map(typeId => ({
                typeId,
                lines: [
                    {
                        qty: getDefaultNeededCount(typeId),
                        start: extrasTimeOverrides[typeId]?.start || undefined,
                        end: extrasTimeOverrides[typeId]?.end || undefined,
                    },
                    ...slotsForType(typeId).map(s => ({ qty: s.qty || 0, start: s.start || undefined, end: s.end || undefined })),
                ].filter(l => l.qty > 0),
            }));

            const extras_requests: any[] = [];
            for (const { typeId, lines } of needLinesByType) {
                // partner_id ne doit PAS dépendre d'une personne précise cliquée en pastille — sinon un
                // besoin "quantité seule" (aucune personne sélectionnée) ne générait jamais de demande
                // externe, silencieusement ignorée côté backend (partner_id vide). Le partenaire du type
                // est toujours connu depuis le catalogue (allExtraTypesFlat), qu'une personne précise ait
                // été choisie ou non. Bug remonté par Emeni (2026-08-20) : un seul type sur plusieurs
                // bookés partait bien en demande, les autres (sans personne cliquée) disparaissaient.
                const flatType = allExtraTypesFlat.find(t => t.id === typeId);
                const suggestion = extrasSelections.find(s => s.kind === 'external' && s.extraTypeId === typeId);
                // Les salariés internes déjà assignés couvrent d'abord l'horaire principal, puis les
                // sous-groupes dans l'ordre : on ne redemande jamais au partenaire ce qui est déjà couvert
                // en interne, mais il faut bien décider À QUEL horaire l'imputer, sans quoi la déduction
                // s'appliquerait plusieurs fois (une par sous-groupe).
                let internalLeft = internal_extras.filter(ie => ie.extra_type_id === typeId).length;
                lines.forEach((line, slotIndex) => {
                    const lineQty = Math.max(0, line.qty);
                    const covered = Math.min(internalLeft, lineQty);
                    internalLeft -= covered;
                    const quantity = lineQty - covered;
                    if (quantity <= 0) return;
                    extras_requests.push({
                        partner_id: suggestion?.partnerId || flatType?.partnerId || '',
                        extra_type_id: typeId,
                        quantity,
                        need_slot_index: slotIndex,
                        // Horaire passé explicitement plutôt que laissé à deviner au backend : à la création
                        // d'une privatisation, les demandes sont écrites AVANT les lignes de besoin, il n'a
                        // donc encore rien à relire pour retrouver l'horaire du sous-groupe.
                        start_time_override: line.start,
                        end_time_override: line.end,
                        // Les personnes cliquées ne sont rattachées qu'à l'horaire principal : cet écran ne
                        // permet pas de choisir qui vient sur quel sous-groupe (c'est le rôle de "Booker"),
                        // et les recopier sur chaque ligne suggérerait les mêmes personnes deux fois.
                        person_ids: slotIndex === 0 && suggestion && suggestion.personIds.length > 0 ? suggestion.personIds : undefined,
                    });
                });
            }

            // Besoin cible RAW, jamais net des internes déjà assignés — contrairement aux demandes ci-dessus,
            // qui doivent, elles, rester nettes puisqu'on ne redemande pas au partenaire ce qui est déjà
            // couvert en interne. Bug réel corrigé (2026-08-20) : soustraire l'interne ici resoustrayait le
            // même salarié à CHAQUE sauvegarde (le total stocké était déjà net la fois précédente), érodant
            // silencieusement le besoin affiché à chaque réouverture — "la bulle ne se met pas bien à jour"
            // signalé par Emeni.
            // Une ligne par sous-groupe horaire (demande d'Emeni 2026-08-22 : pouvoir saisir un horaire
            // différent pour une partie seulement du besoin d'un poste), tirée du MÊME tableau que les
            // demandes pour que les rangs des sous-groupes concordent des deux côtés.
            const extras_needs = needLinesByType.flatMap(({ typeId, lines }) => lines.map(l => ({
                extra_type_id: typeId,
                needed_count: l.qty,
                start_time_override: l.start,
                end_time_override: l.end,
            })));

            const payload = {
                calendar_id: calendarId,
                type,
                phone: formData.phone,
                email: formData.email,
                first_name: formData.firstName,
                last_name: formData.lastName,
                company_name: formData.companyName,
                organizer_name: formData.organizerName,
                start_time,
                end_time,
                num_people: formData.numPeople.trim() ? (parseInt(formData.numPeople) || 0) : null,
                space_ids: fallbackSpaceId ? [fallbackSpaceId] : [],
                staff_requests,
                assignments,
                note_text: noteText,
                documents,
                taken_by_id: trackTakenBy ? (takenById || null) : null,
                crm_contact_id: formData.crmContactId || null,
                extras_requests,
                internal_extras,
                extras_needs,
                auto_send_extras: autoSendExtras,
                // Bascule définitivement la bulle du calendrier de "Besoin : N Type" vers "Type : ... N extras"
                // dès la première sauvegarde faite depuis le bouton "Booker" — même sans rien sélectionner.
                // Ne redescend jamais côté backend (voir MAX(...) dans PUT /evenementiel/:id).
                extras_booked: isBookerMode
            };

            if (isEditMode) {
                const res: any = await moduleApi.updateEvenementiel(initialEvent.id, payload);
                // Besoin baissé en dessous de ce qui est déjà envoyé/confirmé chez un partenaire — jamais
                // annulé automatiquement, juste signalé (décision validée avec Emeni, 2026-08-20).
                if (Array.isArray(res?.over_committed) && res.over_committed.length > 0) {
                    const lines = res.over_committed.map((o: any) => `${o.type_label} : besoin ${o.needed_count}, déjà ${o.committed_count} envoyé(s)/confirmé(s)`);
                    alert(`Attention, le besoin est en dessous de ce qui est déjà engagé auprès du partenaire (rien n'a été annulé automatiquement) :\n\n${lines.join('\n')}`);
                }
            } else {
                await moduleApi.createEvenementiel(payload);
            }
            onSuccess();
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const totalExtras = staffingRequirements.reduce((acc, req) => {
        return acc + Math.max(0, req.needed - req.assignedEmployeeIds.length);
    }, 0);

    const tabs = [
        { id: 'infos', label: 'Infos & Staffing', icon: User },
        { id: 'documents', label: 'Documents', icon: FileText },
        { id: 'notes', label: 'Messages / Notes', icon: MessageSquare }
    ] as const;

    return (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 flex items-center justify-center p-4 transition-colors duration-200">
            <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-6xl max-h-[90vh] bg-white dark:bg-surface rounded-3xl border border-border shadow-2xl overflow-hidden flex flex-col transition-colors duration-200"
            >
                {/* Header */}
                <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-surface-soft transition-colors duration-200 shrink-0">
                    <div>
                        <h2 className="text-xl font-bold text-slate-900 dark:text-content">{isEditMode ? 'Modifier la Privatisation' : 'Nouvelle Privatisation'}</h2>
                        <div className="flex gap-4 mt-2">
                            <button 
                                onClick={() => setType('PRIVÉ')}
                                className={`text-xs font-bold uppercase tracking-widest pb-1 border-b-2 transition-all ${type === 'PRIVÉ' ? 'border-slate-900 dark:border-white text-slate-900 dark:text-content' : 'border-transparent text-slate-500 dark:text-content-muted'}`}
                            >
                                Particulier
                            </button>
                            <button 
                                onClick={() => setType('PROFESSIONNEL')}
                                className={`text-xs font-bold uppercase tracking-widest pb-1 border-b-2 transition-all ${type === 'PROFESSIONNEL' ? 'border-slate-900 dark:border-white text-slate-900 dark:text-content' : 'border-transparent text-slate-500 dark:text-content-muted'}`}
                            >
                                Professionnel
                            </button>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-200 dark:hover:bg-surface-soft rounded-full text-slate-500 dark:text-content-muted transition-colors duration-200">
                        <X size={24} />
                    </button>
                </div>

                <div className="px-6 py-4 overflow-y-auto flex-1">
                    <div className="grid grid-cols-3 gap-2 bg-surface p-1.5 rounded-xl border border-gray-300 dark:border-border transition-colors duration-200 mb-4">
                        {tabs.map(tab => {
                            const Icon = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    type="button"
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-[10px] font-bold transition-all ${
                                        activeTab === tab.id
                                            ? 'bg-brand-600 hover:bg-brand-700 text-brand-fg'
                                            : 'text-slate-500 dark:text-content-muted hover:bg-slate-100 dark:hover:bg-surface-soft'
                                    }`}
                                >
                                    <Icon size={14} />
                                    <span>{tab.label}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="px-6 py-4 space-y-3 overflow-y-auto flex-1">
                    {error && (
                        <div className="p-3 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl flex items-center gap-2 text-sm">
                            <AlertCircle size={16} />
                            <p className="font-medium">{error}</p>
                        </div>
                    )}

                    {activeTab === 'infos' && (
                        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                            <div className="lg:col-span-3 space-y-4">
                            <section className="space-y-2">
                                <h3 className="text-[11px] font-bold text-slate-600 dark:text-content-muted uppercase tracking-widest flex items-center gap-2">
                                    <User size={12} /> Infos Client
                                </h3>
                                <div className="grid grid-cols-2 gap-2">
                                    {type === 'PRIVÉ' ? (
                                        <>
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-bold text-slate-700 dark:text-gray-600 uppercase tracking-widest">Prénom</label>
                                                <input 
                                                    type="text"
                                                    value={formData.firstName}
                                                    onChange={e => {
                                                        const value = e.target.value;
                                                        setFormData({...formData, firstName: value, crmContactId: ''});
                                                        triggerLookup('firstName', value);
                                                    }}
                                                    onBlur={() => setTimeout(() => setLookupField(null), 120)}
                                                    className="w-full bg-surface border border-gray-300 dark:border-border rounded-lg px-3 py-1.5 text-xs text-slate-900 dark:text-content outline-none focus:border-slate-400 dark:focus:border-border transition-colors duration-200"
                                                />
                                                {renderCRMSearchSuggestions('firstName')}
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-bold text-slate-600 dark:text-gray-600 uppercase tracking-widest">Nom</label>
                                                <input 
                                                    type="text" required
                                                    value={formData.lastName}
                                                    onChange={e => {
                                                        const value = e.target.value;
                                                        setFormData({...formData, lastName: value, crmContactId: ''});
                                                        triggerLookup('lastName', value);
                                                    }}
                                                    onBlur={() => setTimeout(() => setLookupField(null), 120)}
                                                    className="w-full bg-surface border border-gray-300 dark:border-border rounded-lg px-3 py-1.5 text-xs text-slate-900 dark:text-content outline-none focus:border-slate-400 dark:focus:border-border transition-colors duration-200"
                                                />
                                                {renderCRMSearchSuggestions('lastName')}
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-bold text-slate-600 dark:text-gray-600 uppercase tracking-widest">Entreprise</label>
                                                <input 
                                                    type="text" required
                                                    value={formData.companyName}
                                                    onChange={e => {
                                                        const value = e.target.value;
                                                        setFormData({...formData, companyName: value, crmContactId: ''});
                                                        triggerLookup('companyName', value);
                                                    }}
                                                    onBlur={() => setTimeout(() => setLookupField(null), 120)}
                                                    className="w-full bg-surface border border-gray-300 dark:border-border rounded-lg px-3 py-1.5 text-xs text-slate-900 dark:text-content outline-none focus:border-slate-400 dark:focus:border-border transition-colors duration-200"
                                                />
                                                {renderCRMSearchSuggestions('companyName')}
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-bold text-slate-600 dark:text-gray-600 uppercase tracking-widest">Organisateur</label>
                                                <input 
                                                    type="text"
                                                    value={formData.organizerName}
                                                    onChange={e => setFormData({...formData, organizerName: e.target.value})}
                                                    className="w-full bg-surface border border-gray-300 dark:border-border rounded-lg px-3 py-1.5 text-xs text-slate-900 dark:text-content outline-none focus:border-slate-400 dark:focus:border-border transition-colors duration-200"
                                                />
                                            </div>
                                        </>
                                    )}
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-slate-600 dark:text-gray-600 uppercase tracking-widest">Téléphone</label>
                                        <input 
                                            type="tel"
                                            value={formData.phone}
                                            onChange={e => {
                                                const value = e.target.value;
                                                setFormData({...formData, phone: value});
                                                triggerLookup('phone', value);
                                            }}
                                            onBlur={() => setTimeout(() => setLookupField(null), 120)}
                                            className="w-full bg-surface border border-gray-300 dark:border-border rounded-lg px-3 py-1.5 text-xs text-slate-900 dark:text-content outline-none focus:border-slate-400 dark:focus:border-border transition-colors duration-200"
                                        />
                                        {renderCRMSearchSuggestions('phone')}
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-slate-600 dark:text-gray-600 uppercase tracking-widest">Email</label>
                                        <input 
                                            type="email"
                                            value={formData.email}
                                            onChange={e => {
                                                const value = e.target.value;
                                                setFormData({...formData, email: value});
                                                triggerLookup('email', value);
                                            }}
                                            onBlur={() => setTimeout(() => setLookupField(null), 120)}
                                            className="w-full bg-surface border border-gray-300 dark:border-border rounded-lg px-3 py-1.5 text-xs text-slate-900 dark:text-content outline-none focus:border-slate-400 dark:focus:border-border transition-colors duration-200"
                                        />
                                        {renderCRMSearchSuggestions('email')}
                                    </div>
                                </div>
                            </section>

                            <section className="space-y-2">
                                <h3 className="text-[11px] font-bold text-slate-500 dark:text-content-muted uppercase tracking-widest flex items-center gap-2">
                                    <Clock size={14} /> Logistique
                                </h3>
                                <div className="grid grid-cols-2 gap-2">
                                    <div className="space-y-1 col-span-2">
                                        <label className="text-[10px] font-bold text-slate-600 dark:text-gray-600 uppercase tracking-widest">Date de l'événement</label>
                                        <input 
                                            type="date"
                                            min={minDate}
                                            max={maxDate}
                                            value={formData.date}
                                            onChange={e => setFormData({...formData, date: e.target.value})}
                                            className="w-full bg-surface border border-gray-300 dark:border-border rounded-lg px-3 py-1.5 text-xs text-slate-900 dark:text-content outline-none focus:border-slate-400 dark:focus:border-border transition-colors duration-200"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-slate-600 dark:text-gray-600 uppercase tracking-widest">Heure de début</label>
                                        <input 
                                            type="time" required
                                            value={formData.startTime}
                                            onChange={e => setFormData({...formData, startTime: e.target.value})}
                                            className="w-full bg-surface border border-gray-300 dark:border-border rounded-lg px-3 py-1.5 text-xs text-slate-900 dark:text-content outline-none focus:border-slate-400 dark:focus:border-border transition-colors duration-200"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-slate-600 dark:text-gray-600 uppercase tracking-widest">Heure de fin</label>
                                        <input 
                                            type="time" required
                                            value={formData.endTime}
                                            onChange={e => setFormData({...formData, endTime: e.target.value})}
                                            className="w-full bg-surface border border-gray-300 dark:border-border rounded-lg px-3 py-1.5 text-xs text-slate-900 dark:text-content outline-none focus:border-slate-400 dark:focus:border-border transition-colors duration-200"
                                        />
                                    </div>
                                    <div className="space-y-1 col-span-2">
                                        <label className="text-[10px] font-bold text-slate-600 dark:text-gray-600 uppercase tracking-widest">Nombre de personnes</label>
                                        <input 
                                            type="number"
                                            value={formData.numPeople}
                                            onChange={e => setFormData({...formData, numPeople: e.target.value})}
                                            className="w-full bg-surface border border-gray-300 dark:border-border rounded-lg px-3 py-1.5 text-xs text-slate-900 dark:text-content outline-none focus:border-slate-400 dark:focus:border-border transition-colors duration-200"
                                        />
                                    </div>
                                </div>
                            </section>

                            <section className="space-y-2">
                                <h3 className="text-[11px] font-bold text-slate-500 dark:text-content-muted uppercase tracking-widest flex items-center gap-2">
                                    <MapPin size={14} /> Espaces
                                </h3>
                                {spaces.length === 0 ? (
                                    <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm">
                                        Aucun espace configuré pour ce client. La privatisation peut être enregistrée sans sélection d'espace.
                                    </div>
                                ) : (
                                    <>
                                        <p className="text-[10px] font-bold text-slate-500 dark:text-content-muted uppercase tracking-widest">
                                            Sélection d'espace obligatoire
                                        </p>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                    {spaces.map(space => (
                                                <button
                                                    key={space.id}
                                                    type="button"
                                                    onClick={() => setFormData(prev => ({ ...prev, selectedSpaceId: space.id }))}
                                                    className={`px-3 py-1.5 rounded-lg border transition-all text-xs font-bold flex items-center gap-2 ${
                                                        formData.selectedSpaceId === space.id
                                                            ? 'bg-brand-600 hover:bg-brand-700 text-brand-fg border-black dark:border-white'
                                                            : 'bg-surface border-gray-300 dark:border-border text-slate-500 dark:text-content-muted hover:border-slate-400 dark:hover:border-white/30'
                                                    }`}
                                                >
                                                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: space.color_hex || space.color }} />
                                                    {space.name}
                                                </button>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </section>

                            {trackTakenBy && (
                                <section className="space-y-2">
                                    <h3 className="text-[10px] font-bold text-slate-500 dark:text-content-muted uppercase tracking-widest">Privatisation prise par</h3>
                                    <select
                                        value={takenById}
                                        onChange={(e) => setTakenById(e.target.value)}
                                        className="w-full bg-surface border border-gray-300 dark:border-border rounded-lg px-3 py-1.5 text-xs text-slate-900 dark:text-content outline-none focus:border-slate-400 dark:focus:border-border transition-colors duration-200"
                                    >
                                        <option value="">Sélectionner un preneur</option>
                                        {allowedTakenByEmployees.map((emp) => {
                                            const fullName = [emp.first_name, emp.last_name].filter(Boolean).join(' ').trim() || emp.email || emp.id;
                                            return (
                                                <option key={emp.id} value={emp.id}>{fullName}</option>
                                            );
                                        })}
                                    </select>
                                </section>
                            )}

                            {extrasPartners.length > 0 && (
                                <label className="flex items-center gap-2.5 cursor-pointer">
                                    <input type="checkbox" checked={autoSendExtras} onChange={(e) => setAutoSendExtras(e.target.checked)}
                                        className="w-4 h-4 rounded border-gray-300 dark:border-border accent-brand-600" />
                                    <span className="text-xs text-slate-700 dark:text-content">Envoi auto. des demandes aux prestataires une fois les extras bookés</span>
                                </label>
                            )}
                        </div>

                        <div className="lg:col-span-2 space-y-4">
                        {(extrasPartners.length > 0 || extrasInternalTypes.length > 0) && (() => {
                            // Un type ne s'affiche en sélection (externe ou interne) que si un besoin a été
                            // indiqué pour lui — sinon rien à booker. Une fois affiché, il le reste même une
                            // fois le besoin entièrement couvert (sinon impossible de désélectionner une
                            // pastille déjà choisie) : demande de simplification d'Emeni (2026-08-19, 2e refonte).
                            const externalTypesWithNeed = allExtraTypesFlat.filter(t => getNeededCount(t.id) > 0);
                            const internalTypesWithNeed = extrasInternalTypes.filter(t => getNeededCount(t.id) > 0);
                            return (
                            <div className="space-y-4 bg-slate-50 dark:bg-white/[0.02] p-4 rounded-2xl border border-border transition-colors duration-200">
                                <h3 className="text-[11px] font-bold text-slate-500 dark:text-content-muted uppercase tracking-widest flex items-center gap-2">
                                    <HardHat size={14} /> Extras requis
                                </h3>

                                {allExtraTypesFlat.length > 0 && (
                                    <div className="space-y-1.5 pb-3 border-b border-border">
                                        <div className="flex items-center justify-between">
                                            <label className="text-[10px] font-bold text-slate-500 dark:text-content-muted uppercase tracking-widest">Besoins</label>
                                            {isEditMode && (
                                                <button type="button" onClick={toggleNeedsHistory} className="text-[10px] font-bold text-brand-600 dark:text-brand-400 hover:underline">
                                                    {showNeedsHistory ? 'Masquer l\'historique' : 'Historique'}
                                                </button>
                                            )}
                                        </div>
                                        {allExtraTypesFlat.map(t => {
                                            const current = getNeededCount(t.id);
                                            const initial = initialNeededCounts[t.id] ?? current;
                                            const committed = committedExternalByType[t.id] || 0;
                                            const resteABooker = Math.max(0, remainingForType(t.id) - committed);
                                            return (
                                                <div key={t.id} className="space-y-0.5">
                                                    <div className="flex items-center justify-between gap-2">
                                                        <span className="text-xs text-slate-700 dark:text-content">
                                                            {t.label}
                                                            {current !== initial && (
                                                                <span className="ml-1.5 text-[10px] font-bold text-brand-600 dark:text-brand-400">{initial} → {current}</span>
                                                            )}
                                                        </span>
                                                        <div className="flex items-center gap-2 shrink-0">
                                                            <button type="button" onClick={() => setNeededCount(t.id, getNeededCount(t.id) - 1)}
                                                                className="w-6 h-6 flex items-center justify-center rounded-lg border border-gray-300 dark:border-border text-slate-500 dark:text-content-muted hover:border-brand-500/40 font-bold">−</button>
                                                            <span className="w-6 text-center text-sm font-bold text-slate-900 dark:text-content">{remainingForType(t.id)}</span>
                                                            <button type="button" onClick={() => setNeededCount(t.id, getNeededCount(t.id) + 1)}
                                                                className="w-6 h-6 flex items-center justify-center rounded-lg border border-gray-300 dark:border-border text-slate-500 dark:text-content-muted hover:border-brand-500/40 font-bold">+</button>
                                                        </div>
                                                    </div>
                                                    {isBookerMode && committed > 0 && (
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-[10px] text-slate-500 dark:text-content-muted">{committed} déjà envoyé(s)/confirmé(s) chez le partenaire</span>
                                                            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-brand-500/10 text-brand-600 dark:text-brand-400">Reste à booker : {resteABooker}</span>
                                                        </div>
                                                    )}
                                    {current > 0 && (
                                                        <div className="flex items-center gap-2 pt-0.5 flex-wrap">
                                                            <span className="text-[10px] text-slate-500 dark:text-content-muted shrink-0">Horaire de cet extra :</span>
                                                            <input type="time" value={extrasTimeOverrides[t.id]?.start || ''}
                                                                onChange={(e) => setExtrasTimeOverrides(prev => ({ ...prev, [t.id]: { start: e.target.value, end: prev[t.id]?.end || '' } }))}
                                                                className="bg-surface border border-gray-300 dark:border-border rounded-md px-1.5 py-0.5 text-[10px] text-slate-900 dark:text-content outline-none focus:border-brand-500" />
                                                            <span className="text-[10px] text-slate-400">→</span>
                                                            <input type="time" value={extrasTimeOverrides[t.id]?.end || ''}
                                                                onChange={(e) => setExtrasTimeOverrides(prev => ({ ...prev, [t.id]: { start: prev[t.id]?.start || '', end: e.target.value } }))}
                                                                className="bg-surface border border-gray-300 dark:border-border rounded-md px-1.5 py-0.5 text-[10px] text-slate-900 dark:text-content outline-none focus:border-brand-500" />
                                                            <span className="text-[9px] text-slate-400 italic">vide = horaire de l'événement</span>
                                                        </div>
                                                    )}
                                                    {slotsForType(t.id).map(slot => (
                                                        <div key={slot.uid} className="flex items-center gap-2 pt-0.5 flex-wrap">
                                                            <span className="text-[10px] text-slate-500 dark:text-content-muted shrink-0">+ horaire différent :</span>
                                                            <input type="number" min={0} value={slot.qty}
                                                                onChange={(e) => updateNeedSlot(slot.uid, { qty: parseInt(e.target.value) || 0 })}
                                                                className="w-12 bg-surface border border-gray-300 dark:border-border rounded-md px-1.5 py-0.5 text-[10px] text-slate-900 dark:text-content outline-none focus:border-brand-500" />
                                                            <input type="time" value={slot.start}
                                                                onChange={(e) => updateNeedSlot(slot.uid, { start: e.target.value })}
                                                                className="bg-surface border border-gray-300 dark:border-border rounded-md px-1.5 py-0.5 text-[10px] text-slate-900 dark:text-content outline-none focus:border-brand-500" />
                                                            <span className="text-[10px] text-slate-400">→</span>
                                                            <input type="time" value={slot.end}
                                                                onChange={(e) => updateNeedSlot(slot.uid, { end: e.target.value })}
                                                                className="bg-surface border border-gray-300 dark:border-border rounded-md px-1.5 py-0.5 text-[10px] text-slate-900 dark:text-content outline-none focus:border-brand-500" />
                                                            <button type="button" onClick={() => removeNeedSlot(slot.uid)}
                                                                className="text-slate-400 hover:text-red-400 font-bold text-xs">×</button>
                                                        </div>
                                                    ))}
                                                    {current > 0 && (
                                                        <button type="button" onClick={() => addNeedSlot(t.id)}
                                                            className="text-[10px] font-bold text-brand-600 dark:text-brand-400 hover:underline pt-0.5">
                                                            + Ajouter un horaire différent pour une partie du groupe
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        <p className="text-[10px] text-slate-500 dark:text-content-muted">
                                            Affiché dans la bulle du calendrier tant qu'aucun extra n'est assigné — descend automatiquement à chaque sélection ci-dessous.
                                            {!isBookerMode && ' Modifier ce chiffre ici ne prévient pas le partenaire : seul "Booker" convertit une hausse en vraie demande.'}
                                        </p>
                                        {isBookerMode && (() => {
                                            const toSend = allExtraTypesFlat
                                                .map(t => ({ label: t.label, qty: Math.max(0, remainingForType(t.id) - (committedExternalByType[t.id] || 0)) }))
                                                .filter(t => t.qty > 0);
                                            return toSend.length > 0 ? (
                                                <div className="bg-brand-500/10 border border-brand-500/20 rounded-xl p-3 space-y-1.5">
                                                    <p className="text-[10px] font-bold text-brand-600 dark:text-brand-400 uppercase tracking-widest">En enregistrant, ceci part en nouvelle demande</p>
                                                    <ul className="space-y-0.5">
                                                        {toSend.map((t, idx) => (
                                                            <li key={idx} className="text-xs text-slate-700 dark:text-content">{t.qty} x {t.label}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            ) : null;
                                        })()}
                                        {showNeedsHistory && (
                                            <div className="pt-2 space-y-1">
                                                {loadingNeedsHistory ? (
                                                    <p className="text-[10px] text-slate-500 dark:text-content-muted italic">Chargement...</p>
                                                ) : extrasNeedsHistory.length === 0 ? (
                                                    <p className="text-[10px] text-slate-500 dark:text-content-muted italic">Aucune modification enregistrée pour l'instant.</p>
                                                ) : extrasNeedsHistory.map((h, idx) => (
                                                    <p key={idx} className="text-[10px] text-slate-500 dark:text-content-muted">
                                                        {new Date(h.changed_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })} — {h.type_label} : <span className="font-bold text-slate-700 dark:text-content">{h.old_count} → {h.new_count}</span>
                                                    </p>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {!canSelectExtras && (extrasPartners.length > 0 || extrasInternalTypes.length > 0) && (
                                    <p className="text-xs text-slate-500 dark:text-content-muted italic">La sélection des personnes/partenaires se fait depuis le bouton "Booker", pas "Modifier".</p>
                                )}

                                {canSelectExtras && extrasPartners.length > 0 && (
                                    <div className="space-y-2.5">
                                        <h4 className="text-[10px] font-bold text-slate-500 dark:text-content-muted uppercase tracking-widest">Extras externe</h4>
                                        {externalTypesWithNeed.length === 0 ? (
                                            <p className="text-xs text-slate-500 dark:text-content-muted italic">Indiquez d'abord un besoin ci-dessus.</p>
                                        ) : externalTypesWithNeed.map(t => {
                                            const people = extrasPeopleCache[t.id] || [];
                                            const selectedPersonIds = extrasSelections.find(s => s.kind === 'external' && s.extraTypeId === t.id)?.personIds || [];
                                            return (
                                                <div key={t.id} className="space-y-1">
                                                    <p className="text-xs font-semibold text-slate-700 dark:text-content">{t.label}</p>
                                                    {people.length === 0 ? (
                                                        <p className="text-[10px] text-slate-500 dark:text-content-muted italic">Aucune personne enregistrée pour ce type — depuis la fiche du partenaire.</p>
                                                    ) : (
                                                        <div className="flex flex-wrap gap-1.5">
                                                            {people.map(p => {
                                                                const isSelected = selectedPersonIds.includes(p.id);
                                                                return (
                                                                    <button type="button" key={p.id} onClick={() => toggleExternalPerson(t.id, t.partnerId, p.id)}
                                                                        className={`text-[10px] font-semibold rounded-full px-2.5 py-1 border transition-colors ${isSelected ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'bg-surface border-gray-300 dark:border-border text-slate-700 dark:text-content hover:border-brand-500/40'}`}>
                                                                        {p.first_name} {p.last_name}{p.already_used ? ' ·' : ''}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {canSelectExtras && extrasInternalTypes.length > 0 && (
                                    <div className="space-y-2.5">
                                        <h4 className="text-[10px] font-bold text-slate-500 dark:text-content-muted uppercase tracking-widest">Extras interne</h4>
                                        {internalTypesWithNeed.length === 0 ? (
                                            <p className="text-xs text-slate-500 dark:text-content-muted italic">Indiquez d'abord un besoin ci-dessus.</p>
                                        ) : internalTypesWithNeed.map(t => {
                                            const selectedEmployeeIds = extrasSelections.find(s => s.kind === 'internal' && s.internalTypeId === t.id)?.employeeIds || [];
                                            return (
                                                <div key={t.id} className="space-y-1">
                                                    <p className="text-xs font-semibold text-slate-700 dark:text-content">{t.label}</p>
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {t.employees.map(e => {
                                                            const isSelected = selectedEmployeeIds.includes(e.employee_id);
                                                            return (
                                                                <button type="button" key={e.employee_id} onClick={() => toggleInternalEmployee(t.id, e.employee_id)}
                                                                    className={`text-[10px] font-semibold rounded-full px-2.5 py-1 border transition-colors ${isSelected ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'bg-surface border-gray-300 dark:border-border text-slate-700 dark:text-content hover:border-brand-500/40'}`}>
                                                                    {`${e.first_name} ${e.last_name}`.trim()}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                <p className="text-[10px] text-slate-500 dark:text-content-muted">Rien n'est envoyé ni confirmé automatiquement — à valider ensuite depuis le module Extras (envoi au partenaire pour l'externe, confirmation pour l'interne).</p>
                            </div>
                            );
                        })()}
                        </div>

                        </div>
                    )}

                    {activeTab === 'documents' && (
                        <div className="space-y-6 bg-white dark:bg-surface transition-colors duration-200">
                            <div className="p-6 rounded-2xl border border-gray-300 dark:border-border bg-surface/50 space-y-4 transition-colors duration-200">
                                <p className="text-sm text-slate-500 dark:text-content-muted">Ajoutez des pièces jointes dès la création ou pendant la modification.</p>
                                <label className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold cursor-pointer hover:opacity-90 transition-colors duration-200">
                                    <Paperclip size={16} />
                                    <span>Joindre des fichiers</span>
                                    <input type="file" multiple className="hidden" onChange={(e) => addFiles(e.target.files)} />
                                </label>
                            </div>

                            <div className="space-y-3">
                                {documents.length === 0 && (
                                    <div className="p-6 rounded-2xl border border-dashed border-gray-300 dark:border-border text-sm text-slate-500 dark:text-content-muted transition-colors duration-200">
                                        Aucun document lié.
                                    </div>
                                )}
                                {documents.map((doc, idx) => (
                                    <div key={`${doc.file_name}-${idx}`} className="p-4 rounded-2xl border border-gray-300 dark:border-border bg-surface-soft flex items-center justify-between gap-3 transition-colors duration-200">
                                        <div className="min-w-0">
                                            <p className="text-slate-900 dark:text-content font-medium truncate">{doc.file_name}</p>
                                            <p className="text-xs text-slate-500 dark:text-content-muted">
                                                {doc.mime_type || 'Type inconnu'}
                                                {typeof doc.file_size === 'number' ? ` • ${Math.round(doc.file_size / 1024)} Ko` : ''}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={() => openDocument(doc)}
                                                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-border text-slate-500 dark:text-content-muted hover:bg-slate-100 dark:hover:bg-surface-soft transition-colors duration-200"
                                                title="Visualiser"
                                            >
                                                <Eye size={16} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => removeDocument(idx)}
                                                className="px-3 py-2 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10"
                                                title="Supprimer"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {activeTab === 'notes' && (
                        <div className="space-y-4">
                            <div className="p-6 rounded-2xl border border-gray-300 dark:border-border bg-surface/50 transition-colors duration-200">
                                <label className="text-[10px] font-bold text-slate-500 dark:text-content-muted uppercase tracking-widest">Message / Note opérationnelle</label>
                                <textarea
                                    value={noteText}
                                    onChange={(e) => setNoteText(e.target.value)}
                                    rows={8}
                                    placeholder="Instructions particulières pour l'équipe, contraintes client, briefing..."
                                    className="mt-3 w-full bg-surface border border-gray-300 dark:border-border rounded-xl px-4 py-3 text-slate-900 dark:text-content outline-none focus:border-slate-400 dark:focus:border-border transition-colors duration-200 resize-none"
                                />
                                <p className="mt-3 text-xs text-slate-500 dark:text-content-muted">Si ce champ contient du texte, le calendrier affichera automatiquement un indicateur rouge.</p>
                            </div>
                        </div>
                    )}

                    <div className="pt-4 mt-2 border-t border-border flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={onClose}>
                            Annuler
                        </Button>
                        <Button type="submit" disabled={loading}>
                            {loading ? 'Enregistrement...' : (
                                <>
                                    <CheckCircle2 size={15} />
                                    <span>Enregistrer</span>
                                </>
                            )}
                        </Button>
                    </div>
                </form>
            </motion.div>
        </div>
    );
};
